/* ==========================================================================
   Luxury Manuka. The only JavaScript file the luxury templates use.
   No libraries, no jQuery, per brief section 3.

   Everything here exists because HTML and CSS genuinely could not do it:

   1. Gallery navigation. Anchor links work without this file, but the browser
      scrolls the whole page to reveal the target, which drags the viewport
      down to the footer. Cancelling the anchor and scrolling only the track
      is the fix, and it is not expressible in CSS.

   2. Active dot after a swipe. CSS can style what was clicked, never what was
      swiped to.

   3. Stopping a vertical wheel from scrolling the track sideways. CSS has no
      lever for this and the overflow workaround breaks scrollTo.

   4. Holding back the producer video until its panel is opened. A closed
      details element does not stop the browser fetching an iframe inside it.

   5. Opening the zoom from the photograph itself, not only from the button.
      A link around the image would be the no-script way to do it, but the
      image sits inside a scroll-snap track, and an anchor there swallows the
      swipe on touch. Reading the tap here keeps the swipe intact.

   6. Landing the zoom on the right photograph and keeping its dots in step.
      The overlay holds every image in one snapped track, so the swipe itself
      is the browser's. What needs code is jumping the track to the image that
      was tapped, marking the dot that is showing, and leaving the stage on
      whatever was reached when the overlay closes.

   7. Revealing the sections as they scroll into view: below the hero on the
      product page, and the hero copy too on the landing page. CSS
      cannot yet tell in every browser when an element enters the viewport, so
      an IntersectionObserver adds one class and CSS does the moving.

   Written so the page still navigates if this file fails to load: every
   control is a real anchor pointing at a real id.
   ========================================================================== */
(function () {
  'use strict';

  function initGallery(root) {
    var track = root.querySelector('[data-gallery-track]');
    if (!track) return;

    var real = Array.prototype.slice.call(track.children);
    if (!real.length) return;

    var n = real.length;
    /* Controls are collected from the whole gallery, not the frame. The dots sit
       inside the frame but the thumbnail strip is a sibling of it, so a frame
       scoped query found the dots and never the thumbnails, which is why the
       active thumbnail never moved. */
    var galleryRoot = root.closest('.lux-gallery') || root;
    var dots = Array.prototype.slice.call(galleryRoot.querySelectorAll('[data-gallery-go]'));
    var zoom = root.querySelector('[data-gallery-zoom]');
    var index = 0;   // logical slide, 0 to n-1
    var pos = 0;     // position in the DOM, which includes the clones

    /* Infinite loop without a rewind.
       A copy of the last slide goes in front and a copy of the first goes at the
       end. Stepping past either end scrolls one slide as usual, then the track
       is silently repositioned onto the real slide once the scroll settles.
       The viewer sees one step. They never see the track race back to the start. */
    if (n > 1) {
      var head = real[n - 1].cloneNode(true);
      var tail = real[0].cloneNode(true);
      [head, tail].forEach(function (clone) {
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('data-clone', '');
        clone.removeAttribute('id');
        Array.prototype.slice.call(clone.querySelectorAll('[id]')).forEach(function (el) {
          el.removeAttribute('id');
        });
      });
      track.insertBefore(head, real[0]);
      track.appendChild(tail);
      pos = 1;
    }

    var all = Array.prototype.slice.call(track.children);

    function offsetOf(p) {
      // Clamped because a fast enough click can ask for a position that is not
      // there, and reading offsetLeft off undefined takes the whole gallery down.
      var i = Math.max(0, Math.min(all.length - 1, p));
      return all[i].offsetLeft - all[0].offsetLeft;
    }

    // a DOM position back to the slide the viewer thinks they are on
    function logical(p) {
      if (n <= 1) return 0;
      if (p === 0) return n - 1;
      if (p === n + 1) return 0;
      return p - 1;
    }

    /* The active thumbnail always leads the strip. Scrolling it to the left edge
       only works for the first half of the list, because past the midpoint the
       strip runs out of room, so the order is rotated instead: the active item
       takes first place and the ones before it wrap around to the end. Same
       logic as the stage, where the last image is followed by the first. */
    function leadWithActive(el) {
      var strip = galleryRoot.querySelector('.lux-gallery__thumbs');
      if (!strip || !el) return;
      var items = Array.prototype.slice.call(strip.children);
      var active = el.closest('.lux-thumb');
      var start = items.indexOf(active);
      if (start < 0) return;
      items.forEach(function (item, i) {
        item.style.order = (i - start + items.length) % items.length;
      });
      strip.scrollLeft = 0;
    }

    function sync() {
      /* Keyed off each control own target, never its position in the list. With
         ten dots followed by ten thumbnails, an index match only ever lit the
         dots. */
      var activeThumb = null;
      dots.forEach(function (control) {
        var on = parseInt(control.getAttribute('data-gallery-go'), 10) === index;
        control.classList.toggle('is-active', on);
        if (on) {
          control.setAttribute('aria-current', 'true');
          if (control.closest('.lux-thumb')) activeThumb = control;
        } else {
          control.removeAttribute('aria-current');
        }
      });
      leadWithActive(activeThumb);

      // Video slides carry no zoom target, so the button hides rather than
      // pointing at nothing.
      if (zoom) {
        var href = real[index].getAttribute('data-zoom');
        if (href) {
          zoom.setAttribute('href', href);
          zoom.hidden = false;
        } else {
          zoom.hidden = true;
        }
      }
    }

    function prefersReducedMotion() {
      return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    /* While a programmatic scroll is in flight the observer below must not touch
       pos. It fires for every slide the track passes, and settle() would then
       read whichever one happened to be under it rather than the slide we asked
       for, landing one short of the wrap. */
    var navigating = false;

    var navGuard = null;
    var tween = null;

    /* The browser own smooth scroll is not dependable on a scroll-snap container.
       Measured on the live page: scrollTo with behavior smooth, and the CSS
       scroll-behavior, both silently do nothing here, sometimes on the first
       call and reliably after a few. Assigning scrollLeft always lands, so the
       glide is driven frame by frame instead. Snapping is switched off for the
       duration because mandatory snapping fights a per frame assignment. */
    function animateTo(left, done) {
      if (tween) cancelAnimationFrame(tween);
      var from = track.scrollLeft;
      var delta = left - from;
      /* A hidden tab freezes requestAnimationFrame, so the glide would never run
         and the stage would sit on the wrong slide until the tab came back. Same
         for a viewer who asked for less motion. Both land immediately instead. */
      if (Math.abs(delta) < 1 || document.hidden || prefersReducedMotion()) {
        track.scrollLeft = left;
        if (done) done();
        return;
      }
      var started = null;
      var snap = track.style.scrollSnapType;
      var behav = track.style.scrollBehavior;
      track.style.scrollSnapType = 'none';
      track.style.scrollBehavior = 'auto';   // belt and braces, see the CSS note
      function frame(now) {
        if (started === null) started = now;
        var p = Math.min(1, (now - started) / 420);
        var e = p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2;
        track.scrollLeft = from + delta * e;
        if (p < 1) {
          tween = requestAnimationFrame(frame);
        } else {
          track.scrollLeft = left;          // land exactly, floats drift
          tween = null;
          track.style.scrollSnapType = snap;
          track.style.scrollBehavior = behav;
          if (done) done();
        }
      }
      tween = requestAnimationFrame(frame);
    }

    function scrollTo(p, smooth) {
      pos = p;
      var left = offsetOf(p);
      if (smooth && !prefersReducedMotion()) {
        navigating = true;
        // If the tween is interrupted, release the flag anyway rather than
        // blocking the observer for good.
        clearTimeout(navGuard);
        navGuard = setTimeout(function () { navigating = false; }, 1000);
        animateTo(left, settle);
        return;
      }
      if (tween) { cancelAnimationFrame(tween); tween = null; track.style.scrollSnapType = ''; }
      track.scrollLeft = left;
    }

    // Once a scroll onto a clone has finished, land on the real slide with no
    // animation. Same pixels on screen, so nothing moves visibly.
    var settleTimer = null;
    function settle() {
      if (tween) return;                    // the glide is still running
      navigating = false;
      if (n <= 1) return;
      if (pos === 0) scrollTo(n, false);
      else if (pos === n + 1) scrollTo(1, false);
      sync();
    }
    function scheduleSettle() {
      if (n <= 1) return;
      if ('onscrollend' in window) return;      // the real event handles it below
      clearTimeout(settleTimer);
      settleTimer = setTimeout(settle, 650);
    }
    if ('onscrollend' in window) {
      track.addEventListener('scrollend', settle);
    }

    // jump straight to a slide, used by the dots and the thumbnails
    function go(i) {
      index = (i % n + n) % n;
      scrollTo(n > 1 ? index + 1 : 0, true);
      sync();
    }

    // one step either way, which is where the loop has to be seamless
    function step(delta) {
      if (n <= 1) return;
      /* Spamming the arrows lands a second click before the silent hop off a
         clone has run, so pos walked past the end of the track and the loop
         broke. Normalise first: if we are sitting on a clone, take the real
         slide it duplicates, instantly, then step from there. */
      if (pos === 0) scrollTo(n, false);
      else if (pos === n + 1) scrollTo(1, false);
      var next = pos + delta;
      index = logical(next);
      scrollTo(next, true);
      sync();
      scheduleSettle();
    }

    /* 3. Wheel over the stage. A horizontal scroller is the one element a browser
          will move sideways from a vertical wheel, so scrolling the page with the
          pointer over the gallery flicked through the images. Vertical gestures are
          handed back to the page. A deliberate sideways gesture still reaches the
          track, and touch swiping is untouched because it fires no wheel event.

          overflow: hidden looks like the CSS answer and is not: scrollLeft can
          still be assigned, but scrollTo with behavior smooth silently does
          nothing, which kills every arrow, dot and thumbnail. */
    track.addEventListener('wheel', function (event) {
      if (Math.abs(event.deltaX) >= Math.abs(event.deltaY)) return;
      event.preventDefault();
      var step = event.deltaY;
      if (event.deltaMode === 1) step *= 16;               // lines
      else if (event.deltaMode === 2) step *= window.innerHeight;
      window.scrollBy(0, step);
    }, { passive: false });

    document.addEventListener('visibilitychange', function () {
      if (document.hidden && tween) {
        cancelAnimationFrame(tween);
        tween = null;
        track.style.scrollSnapType = '';
        track.style.scrollBehavior = '';
        track.scrollLeft = offsetOf(pos);
        settle();
      }
    });

    var gallery = root.closest('.lux-gallery') || root;
    var opener = null;

    /* A swipe that finishes on the photograph still fires a click in some
       engines, and opening a full screen overlay at the end of a swipe is the
       worst thing this gallery could do. A tap counts only when the pointer
       stayed put and the track did not move under it. pointerup runs before
       click, so the flag is always current by the time the handler reads it. */
    var dragged = false;
    var downX = 0, downY = 0, downLeft = 0;
    track.addEventListener('pointerdown', function (event) {
      downX = event.clientX;
      downY = event.clientY;
      downLeft = track.scrollLeft;
      dragged = false;
    });
    track.addEventListener('pointerup', function (event) {
      dragged = Math.abs(event.clientX - downX) > 10 ||
                Math.abs(event.clientY - downY) > 10 ||
                Math.abs(track.scrollLeft - downLeft) > 4;
    });

    function zoomAt(overlay) {
      var zt = overlay.querySelector('[data-zoom-track]');
      if (!zt || !zt.clientWidth) return 0;
      return Math.round(zt.scrollLeft / zt.clientWidth);
    }

    function syncZoomDots(overlay) {
      if (!overlay) return;
      var at = zoomAt(overlay);
      Array.prototype.slice.call(overlay.querySelectorAll('[data-zoom-go]'))
        .forEach(function (dot, i) { dot.classList.toggle('is-active', i === at); });
    }

    function zoomGo(overlay, to) {
      var zt = overlay.querySelector('[data-zoom-track]');
      if (!zt) return;
      /* Instant, never smooth. A smooth scroll on a snap container is a no-op
         in this engine, and even where it works it would animate through every
         photograph between the two. */
      var prev = zt.style.scrollBehavior;
      zt.style.scrollBehavior = 'auto';
      zt.scrollLeft = to * zt.clientWidth;
      zt.style.scrollBehavior = prev;
      syncZoomDots(overlay);
    }

    /* The id now belongs to a slide inside the one overlay, not to an overlay
       of its own. That is what lets the viewer swipe from the third photograph
       to the fourth instead of closing and reopening. */
    function openZoom(id) {
      var slide = document.getElementById(id);
      if (!slide) return false;
      var overlay = slide.closest('.lux-zoom');
      if (!overlay) return false;
      overlay.classList.add('is-open');
      document.documentElement.classList.add('lux-zoom-open');
      /* The track has no width until the overlay is displayed, so the jump is
         made after the class lands. Reading offsetLeft forces that layout. */
      var zt = overlay.querySelector('[data-zoom-track]');
      if (zt) {
        var prev = zt.style.scrollBehavior;
        zt.style.scrollBehavior = 'auto';
        zt.scrollLeft = slide.offsetLeft;
        zt.style.scrollBehavior = prev;
        syncZoomDots(overlay);
      }
      var close = overlay.querySelector('.lux-zoom__close');
      if (close) close.focus();
      return true;
    }

    function closeZoom() {
      var open = gallery.querySelector('.lux-zoom.is-open');
      if (!open) return false;
      /* Leave the stage on whatever was reached in the overlay. Closing onto a
         different photograph than the one on screen reads as a fault. */
      var reached = zoomAt(open);
      open.classList.remove('is-open');
      document.documentElement.classList.remove('lux-zoom-open');
      go(reached);
      if (opener) { opener.focus(); opener = null; }
      return true;
    }

    var zoomTrack = gallery.querySelector('[data-zoom-track]');
    if (zoomTrack) {
      var zoomTick = null;
      zoomTrack.addEventListener('scroll', function () {
        if (zoomTick) return;
        zoomTick = requestAnimationFrame(function () {
          zoomTick = null;
          syncZoomDots(zoomTrack.closest('.lux-zoom'));
        });
      }, { passive: true });
    }

    gallery.addEventListener('click', function (event) {
      var target = event.target;
      if (!target.closest) return;

      var toDot = target.closest('[data-gallery-go]');
      if (toDot) {
        event.preventDefault();
        go(parseInt(toDot.getAttribute('data-gallery-go'), 10));
        return;
      }

      var toStep = target.closest('[data-gallery-step]');
      if (toStep) {
        event.preventDefault();
        step(parseInt(toStep.getAttribute('data-gallery-step'), 10));
        return;
      }

      // Zoom open and close are intercepted for the same reason as the dots.
      // Left as plain anchors they set location.hash, and the browser then
      // scrolls the page to reveal the slide behind the overlay.
      var toZoom = target.closest('[data-gallery-zoom]');
      if (toZoom) {
        var href = toZoom.getAttribute('href') || '';
        if (href.charAt(0) === '#') {
          opener = toZoom;
          if (openZoom(href.slice(1))) event.preventDefault();
        }
        return;
      }

      var toZoomDot = target.closest('[data-zoom-go]');
      if (toZoomDot) {
        event.preventDefault();
        zoomGo(toZoomDot.closest('.lux-zoom'), parseInt(toZoomDot.getAttribute('data-zoom-go'), 10));
        return;
      }

      if (target.closest('.lux-zoom__close') || target.closest('.lux-zoom__scrim')) {
        event.preventDefault();
        closeZoom();
        return;
      }

      /* The track covers the scrim, so a press on the ground either side of the
         photograph would otherwise do nothing at all. */
      if (target.closest('.lux-zoom__slide') && !target.closest('.lux-zoom__img')) {
        event.preventDefault();
        closeZoom();
        return;
      }

      /* The photograph opens the zoom too. A video keeps its own controls, and
         a clone carries the same data-zoom as the slide it was copied from, so
         a tap on either end of the loop lands on the right overlay. */
      var slide = target.closest('.lux-gallery__slide');
      if (slide && !dragged && !target.closest('video, iframe, a, button')) {
        var slideHref = slide.getAttribute('data-zoom') || '';
        if (slideHref.charAt(0) === '#') {
          opener = null;
          if (openZoom(slideHref.slice(1))) event.preventDefault();
        }
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') closeZoom();
    });

    // Someone swiping the track never fires a click, so the dots would go stale.
    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        if (navigating || tween) return;   // a click is driving, do not fight it
        entries.forEach(function (entry) {
          if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
            pos = all.indexOf(entry.target);
            index = logical(pos);
            sync();
            scheduleSettle();
          }
        });
      }, { root: track, threshold: 0.6 });
      all.forEach(function (slide) { observer.observe(slide); });
    }

    // Start on the real first slide rather than the clone sitting in front of it.
    if (n > 1) {
      requestAnimationFrame(function () { scrollTo(1, false); });
      window.addEventListener('resize', function () { scrollTo(pos, false); });
    }

    sync();
  }

  function initQuantity(root) {
    var input = root.querySelector('input[name="quantity"]');
    if (!input) return;
    root.addEventListener('click', function (event) {
      var button = event.target.closest ? event.target.closest('[data-qty]') : null;
      if (!button) return;
      event.preventDefault();
      var step = button.getAttribute('data-qty') === 'plus' ? 1 : -1;
      var next = (parseInt(input.value, 10) || 1) + step;
      input.value = next < 1 ? 1 : next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /* 4. Lazy video. An iframe inside a closed details element is still fetched,
        measured on the live page, so the address waits in data-lazy-src until a
        visitor actually opens the panel. */
  function initLazyFrames(root) {
    Array.prototype.slice.call(root.querySelectorAll('details')).forEach(function (panel) {
      var frame = panel.querySelector('iframe[data-lazy-src]');
      if (!frame) return;
      function load() {
        if (!panel.open) return;
        frame.src = frame.getAttribute('data-lazy-src');
        frame.removeAttribute('data-lazy-src');
        panel.removeEventListener('toggle', load);
      }
      panel.addEventListener('toggle', load);
      load();                       // already open in the theme editor
    });
  }

  /* 7. Scroll reveal. The hidden states only exist under html.lux-motion, and
        only this function adds that class, so a page where it never runs shows
        everything. It stays off in the theme editor, for reduced motion, and
        unless the hero's Reveal setting is on. Brief section 3 rules out
        libraries, so this is the native version of a GSAP reveal. */
  var REVEALS = {
    heading: '.lux-taste__heading, .lux-prov__heading, .lux-pres__heading, .lux-why__heading, .lux-con__heading, .lux-faq__heading, .lux-rel__heading, ' +
      '.lux-lph__heading, .lux-lpe__heading, .lux-lpp__title',
    rise: '.lux-stockist__item, .lux-taste__intro > :not(.lux-taste__heading), .lux-taste__field, .lux-prov__origin > :not(.lux-prov__heading), .lux-prov__fact, .lux-prov__producer, .lux-prov__rarity, .lux-pres__intro > :not(.lux-pres__heading), .lux-point, .lux-why__point, .lux-con__intro > :not(.lux-con__heading), .lux-con__fields, .lux-faq__intro > :not(.lux-faq__heading), .lux-faq__item, .lux-rel__card, ' +
      // landing page. LP3 animates what is inside each card, never the card,
      // whose transform and opacity belong to the stacking script.
      '.lux-lph__text, .lux-lph__actions, .lux-lpe__eyebrow, .lux-lpe__text, .lux-lpe__pillar, .lux-lpp__brand, .lux-lpp__badges, .lux-lpp__edition, .lux-lpp__text, .lux-lpp__btn',
    settle: '.lux-taste__img, .lux-prov__film-img, .lux-pres__img, .lux-con__portrait-img, .lux-lpp__img',
    fill: '.lux-taste__fill'
  };

  function initMotion(root) {
    if (!document.querySelector('[data-lux-motion]')) return;
    if (window.Shopify && window.Shopify.designMode) return;
    if (!('IntersectionObserver' in window)) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var items = [];
    Object.keys(REVEALS).forEach(function (type) {
      Array.prototype.slice.call(root.querySelectorAll(REVEALS[type])).forEach(function (el) {
        if (el.hasAttribute('data-lux-reveal')) return;
        el.setAttribute('data-lux-reveal', type);
        items.push(el);
      });
    });
    if (!items.length) return;
    document.documentElement.classList.add('lux-motion');

    // Whatever crosses the line together is revealed top to bottom, 80ms apart
    // and never more than 400ms after the first. The transition class comes off
    // once the reveal is done, so a card's own hover transition is untouched.
    var observer = new IntersectionObserver(function (entries) {
      entries
        .filter(function (entry) { return entry.isIntersecting; })
        .sort(function (a, b) {
          return (a.boundingClientRect.top - b.boundingClientRect.top) || (a.boundingClientRect.left - b.boundingClientRect.left);
        })
        .forEach(function (entry, i) {
          var el = entry.target;
          var delay = Math.min(i * 80, 400);
          observer.unobserve(el);
          el.style.transitionDelay = delay + 'ms';
          el.classList.add('is-revealing', 'is-revealed');
          setTimeout(function () {
            el.classList.remove('is-revealing');
            el.style.transitionDelay = '';
          }, delay + 1800);
        });
    }, { rootMargin: '0px 0px -12% 0px' });
    items.forEach(function (el) { observer.observe(el); });
  }

  function init(scope) {
    var container = scope || document;
    Array.prototype.slice.call(container.querySelectorAll('[data-gallery]')).forEach(initGallery);
    Array.prototype.slice.call(container.querySelectorAll('.lux-qty')).forEach(initQuantity);
    initLazyFrames(container);
    initMotion(container);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  // Theme editor re-renders a section without reloading the page.
  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
})();
